import { useCallback, useState, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import type { Map as MapboxMap } from "mapbox-gl";
import { SHOW_ACTIVITY_LOG } from "./features";
import { ActivityPanel } from "../activity/ActivityPanel";
import { MapView } from "../map/MapView";
import { MapLayersPanel } from "../map/components/MapLayersPanel";
import { SavedSetCreateDialog } from "../map/components/SavedSetCreateDialog";
import { SavedSetUpdateDialog } from "../map/components/SavedSetUpdateDialog";
import { isAuthenticated, logout } from "../api/auth";
import { useCurrentUserTeamContext } from "../api/users";
import { fetchCampaigns, fetchFlyerTypes, type Campaign, type CampaignFlyer, type FlyerType } from "../api/campaigns";
import { hasMapCampaignScope, isAllCampaignsMode, isCampaignSelected } from "../api/map/campaignScope";
import { MapContextProvider, type SetHighlightedAssignmentAreaOptions } from "../map/MapContext";
import type { AssignmentArea } from "../api/types";
import { MobileMenu } from "./MobileMenu";
import { WwwPortalNavBar } from "./WwwPortalNavBar";
import { usePostalCodes, useElectoralDistricts, useMunicipalities, useCounties } from "../api/map";
import type { AssignmentsResponse } from "../api/map/types";
import { getBoundingBoxFromFeature } from "../api/map/utils";
import {
    useSavedSetsList,
    useSavedSetDetails,
    useDeleteSavedSet,
    useTogglePostalCode,
    useTogglePriorityElectoralDistrict
} from "../api/map-saved-sets/hooks";
import { useUserRoles } from "../api/users";
import { useCurrentUser } from "../api/auth";
import { getHighestRole, getRoleLevel } from "../shared/roleHierarchy";
import {
    useElectoralMetadataFields,
    useMunicipalityMetadataGrid
} from "../api/electoral-metadata/hooks";
import { showError } from "../shared/toast";
import "./mobile-menu.css";

export const Shell = (): JSX.Element => {
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const showMapOnlyActions = location.pathname === "/" || location.pathname === "";

    // Initialize from URL params if present
    const urlCampaignId = searchParams.get("campaignId") || "";
    const urlFlyerTypeId = searchParams.get("flyerTypeId") || null;
    const isMobileApp = searchParams.get("mobile") === "true";
    const urlToken = searchParams.get("token");

    // Log mobile mode for debugging
    console.log("[Shell] URL params - mobile:", searchParams.get("mobile"), "isMobileApp:", isMobileApp, "campaignId:", urlCampaignId, "flyerTypeId:", urlFlyerTypeId, "hasToken:", !!urlToken);

    const [selectedCampaignId, setSelectedCampaignId] = useState<string>(urlCampaignId);
    const [selectedFlyerTypeId, setSelectedFlyerTypeId] = useState<string | null>(urlFlyerTypeId);

    // Handle mobile token in useEffect to avoid timing issues
    // CRITICAL: Must run AFTER campaignId/flyerTypeId are read from URL
    useEffect(() => {
        if (urlToken && isMobileApp) {
            const existingToken = localStorage.getItem("mobileAccessToken");
            if (existingToken !== urlToken) {
                console.log("[Shell] Storing mobile token from URL (length:", urlToken.length, ")");
                localStorage.setItem("mobileAccessToken", urlToken);
                // Remove token from URL for security (don't want it visible in address bar)
                const params = new URLSearchParams(searchParams);
                params.delete("token");
                setSearchParams(params, { replace: true });
            }
        }
    }, [urlToken, isMobileApp, searchParams, setSearchParams]);

    // Debug: Log state initialization
    console.log("[Shell] State initialized - selectedCampaignId:", selectedCampaignId, "selectedFlyerTypeId:", selectedFlyerTypeId);
    const [mapInstance, setMapInstance] = useState<MapboxMap | null>(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
    const [polygonArea, setPolygonArea] = useState<AssignmentArea | null>(null);
    const [highlightedAssignmentArea, setHighlightedAssignmentArea] = useState<AssignmentArea | null>(null);
    const polygonCompleteCallbackRef = useRef<((area: AssignmentArea | null) => void) | undefined>();

    // Map layers visibility state
    const [showPostalCodes, setShowPostalCodes] = useState(false);
    const [showElectoralDistricts, setShowElectoralDistricts] = useState(false);
    const [showMunicipalities, setShowMunicipalities] = useState(false);
    const [showAssignments, setShowAssignments] = useState(false);
    const [assignmentsData, setAssignmentsData] = useState<AssignmentsResponse | null>(null);
    const [postalCodesFilter, setPostalCodesFilter] = useState<string>("");
    const [electoralDistrictsFilter, setElectoralDistrictsFilter] = useState<string>("");
    const [municipalitiesFilter, setMunicipalitiesFilter] = useState<string>("");
    const [clickPostalCodesEnabled, setClickPostalCodesEnabled] = useState(false);
    const [clickElectoralDistrictsEnabled, setClickElectoralDistrictsEnabled] = useState(false);
    const [temporaryPostalCodeIds, setTemporaryPostalCodeIds] = useState<string[]>([]);
    const [temporaryElectoralDistrictIds, setTemporaryElectoralDistrictIds] = useState<string[]>([]);
    const [viewportBbox, setViewportBbox] = useState<string | undefined>();

    // Kartlager: county (län) + municipality + saved set (create/edit context)
    const [selectedCountyId, setSelectedCountyId] = useState<string | null>(null);
    const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<string | null>(null);
    const [selectedSavedSetId, setSelectedSavedSetId] = useState<string | null>(null);
    const [createSavedSetDialogOpen, setCreateSavedSetDialogOpen] = useState(false);
    const [updateSavedSetDialogOpen, setUpdateSavedSetDialogOpen] = useState(false);
    /** Selected electoral metadata field for map choropleth (null = no metadata coloring) */
    const [metadataFieldIdForMap, setMetadataFieldIdForMap] = useState<string | null>(null);
    /** When true, valdistrikt fill uses metadata choropleth (when a field is selected); when false, default purple so you can compare with valda distrikt */
    const [showMetadataOnMap, setShowMetadataOnMap] = useState<boolean>(false);
    const [showMobileLayers, setShowMobileLayers] = useState(false);
    const [isNarrowLayout, setIsNarrowLayout] = useState(() =>
        typeof window !== "undefined" && window.matchMedia("(max-width: 960px)").matches
    );

    const handleToggleAssignments = useCallback((show: boolean) => {
        console.log("[Shell] Toggle 'Visa uppdrag'", {
            nextShowAssignments: show,
            previousShowAssignments: showAssignments,
            selectedMunicipalityId,
            selectedCampaignId,
            selectedFlyerTypeId
        });
        setShowAssignments(show);
    }, [showAssignments, selectedMunicipalityId, selectedCampaignId, selectedFlyerTypeId]);

    // Auto-enable postal + electoral layers when entering saved-set edit mode
    useEffect(() => {
        if (selectedSavedSetId) {
            setShowPostalCodes(true);
            setShowElectoralDistricts(true);
        }
    }, [selectedSavedSetId]);

    // Pre-select municipality in Kartlager when user belongs to a kommunteam (Kretsadmin, avdelningsadmin, utdelare)
    const teamContextQuery = useCurrentUserTeamContext();
    const hasPreselectedRef = useRef(false);
    useEffect(() => {
        if (hasPreselectedRef.current) return;
        const teamContext = teamContextQuery.data;
        if (!teamContext?.municipalityPolygonId || selectedCountyId !== null || selectedMunicipalityId !== null) {
            return;
        }
        setSelectedCountyId(teamContext.countyId ?? null);
        setSelectedMunicipalityId(teamContext.municipalityPolygonId);
        hasPreselectedRef.current = true;
    }, [teamContextQuery.data, selectedCountyId, selectedMunicipalityId]);

    // Sync URL params to state (for mobile app deep linking and URL changes)
    // This ensures that when mobile app sets campaignId/flyerTypeId in URL, it gets picked up by state
    // CRITICAL: This useEffect runs on every URL change AND component mount to ensure state is always in sync
    useEffect(() => {
        let needsUpdate = false;

        // ALWAYS sync on mount or URL change to handle Safari reload cache issues
        // Sync campaignId if different (or if state is empty but URL has value)
        if (urlCampaignId && urlCampaignId !== selectedCampaignId) {
            console.log("[Shell] Syncing URL campaignId to state:", urlCampaignId, "(was:", selectedCampaignId, ")");
            setSelectedCampaignId(urlCampaignId);
            needsUpdate = true;
        } else if (!urlCampaignId && selectedCampaignId) {
            // Clear state if URL has no campaign
            console.log("[Shell] Clearing campaignId state (URL has no campaign)");
            setSelectedCampaignId("");
            needsUpdate = true;
        }

        // Sync flyerTypeId if different (including null)
        if (urlFlyerTypeId !== selectedFlyerTypeId) {
            console.log("[Shell] Syncing URL flyerTypeId to state:", urlFlyerTypeId, "(was:", selectedFlyerTypeId, ")");
            setSelectedFlyerTypeId(urlFlyerTypeId);
            needsUpdate = true;
        }

        if (needsUpdate) {
            console.log("[Shell] State updated from URL - map queries will refresh automatically");
        }
    }, [urlCampaignId, urlFlyerTypeId]); // ONLY depend on URL params, not state!

    // Update URL when campaign or flyer type changes (user interaction in UI)
    useEffect(() => {
        const params = new URLSearchParams(searchParams);

        // Always preserve mobile flag if it was set
        if (isMobileApp) {
            params.set("mobile", "true");
        }

        if (selectedCampaignId) {
            params.set("campaignId", selectedCampaignId);
            if (selectedFlyerTypeId) {
                params.set("flyerTypeId", selectedFlyerTypeId);
            } else {
                params.delete("flyerTypeId");
            }
        } else {
            params.delete("campaignId");
            params.delete("flyerTypeId");
        }
        setSearchParams(params, { replace: true });
    }, [selectedCampaignId, selectedFlyerTypeId, searchParams, setSearchParams, isMobileApp]);

    // Add class to body when mobile app is active (for styling menu drawer)
    useEffect(() => {
        if (isMobileApp) {
            document.body.classList.add("mobile-app-active");
        } else {
            document.body.classList.remove("mobile-app-active");
        }
        return () => {
            document.body.classList.remove("mobile-app-active");
        };
    }, [isMobileApp]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(max-width: 960px)");
        const update = () => setIsNarrowLayout(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    useEffect(() => {
        if (!isNarrowLayout) {
            setShowMobileLayers(false);
        }
    }, [isNarrowLayout]);

    // Expose function for mobile app to update campaign/flyer without full page reload
    useEffect(() => {
        (window as any).cfUpdateCampaign = (campaignId: string, flyerTypeId: string | null) => {
            console.log("[Shell] cfUpdateCampaign called - campaignId:", campaignId, "flyerTypeId:", flyerTypeId);
            console.log("[Shell] Current state - selectedCampaignId:", selectedCampaignId, "selectedFlyerTypeId:", selectedFlyerTypeId);

            const params = new URLSearchParams(searchParams);

            // Always preserve mobile flag
            if (isMobileApp) {
                params.set("mobile", "true");
            }

            // Update campaign and flyer params
            if (campaignId) {
                params.set("campaignId", campaignId);
                if (flyerTypeId) {
                    params.set("flyerTypeId", flyerTypeId);
                } else {
                    params.delete("flyerTypeId");
                }
            } else {
                params.delete("campaignId");
                params.delete("flyerTypeId");
            }

            // Update URL - this will trigger the useEffect above which updates state
            console.log("[Shell] Calling setSearchParams with:", params.toString());
            setSearchParams(params, { replace: true });

            console.log("[Shell] URL updated, state will sync automatically");
            return true;
        };

        // Expose function for mobile app to open URLs in external browser
        (window as any).cfOpenInBrowser = (url: string) => {
            console.log("[Shell] cfOpenInBrowser called with URL:", url);
            // Use iOS message handler if available (WKWebView)
            const webkit = (window as any).webkit;
            if (webkit?.messageHandlers?.openInBrowser) {
                webkit.messageHandlers.openInBrowser.postMessage(url);
            } else {
                // Fallback: try to open in new window (may not work in webview)
                window.open(url, '_blank');
            }
        };

        return () => {
            delete (window as any).cfUpdateCampaign;
            delete (window as any).cfOpenInBrowser;
        };
    }, [searchParams, setSearchParams, isMobileApp, selectedCampaignId, selectedFlyerTypeId]);

    // Check authentication status using a query
    // CRITICAL: Don't retry on 5xx errors (like 503) to avoid request loops
    const { data: isAuth = false } = useQuery<boolean>({
        queryKey: ["auth", "status"],
        queryFn: isAuthenticated,
        retry: (_failureCount, error) => {
            // Never retry authentication checks - if server is down, don't hammer it
            // Also don't retry on 5xx errors (503, 500, etc.) as they indicate server issues
            if (error && typeof error === "object" && "response" in error) {
                const httpError = error as { response?: { status?: number } };
                if (httpError.response?.status && httpError.response.status >= 500) {
                    return false; // Don't retry server errors
                }
                if (httpError.response?.status && httpError.response.status >= 400 && httpError.response.status < 500) {
                    return false; // Don't retry client errors either
                }
            }
            return false; // Never retry auth checks
        },
        retryDelay: 0,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnMount: false, // Don't refetch on mount if we have cached data
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
    });

    const handleMapReady = useCallback((map: MapboxMap) => {
        setMapInstance(map);
        setIsMapLoaded(true);
    }, []);

    const handleStartPolygonDrawing = useCallback(
        (initialArea?: AssignmentArea, onComplete?: (area: AssignmentArea | null) => void) => {
            setIsDrawingPolygon(true);
            setPolygonArea(initialArea || null);
            polygonCompleteCallbackRef.current = onComplete;
        },
        []
    );

    const handleStopPolygonDrawing = useCallback(() => {
        setIsDrawingPolygon(false);
        polygonCompleteCallbackRef.current = undefined;
    }, []);

    const handlePolygonAreaChange = useCallback((area: AssignmentArea | null) => {
        setPolygonArea(area);
        if (polygonCompleteCallbackRef.current) {
            polygonCompleteCallbackRef.current(area);
        }
    }, []);

    const handleHighlightedAssignmentAreaChange = useCallback(
        (area: AssignmentArea | null, _options?: SetHighlightedAssignmentAreaOptions) => {
            setHighlightedAssignmentArea(area);
        },
        []
    );

    const mapContextValue = useMemo(
        () => ({
            map: mapInstance,
            isMapLoaded,
            isDrawingPolygon,
            polygonArea,
            highlightedAssignmentArea,
            highlightedAssignmentId: null as string | null,
            setHighlightedAssignmentArea: handleHighlightedAssignmentAreaChange,
            assignmentHighlightNonce: 0,
            startPolygonDrawing: handleStartPolygonDrawing,
            stopPolygonDrawing: handleStopPolygonDrawing,
            setPolygonArea: handlePolygonAreaChange
        }),
        [
            mapInstance,
            isMapLoaded,
            isDrawingPolygon,
            polygonArea,
            highlightedAssignmentArea,
            handleHighlightedAssignmentAreaChange,
            handleStartPolygonDrawing,
            handleStopPolygonDrawing,
            handlePolygonAreaChange
        ]
    );

    // Fetch campaigns
    const { data: campaigns = [] } = useQuery<Campaign[]>({
        queryKey: ["campaigns"],
        queryFn: fetchCampaigns,
        enabled: isAuth,
        staleTime: 5 * 60 * 1000 // 5 minutes
    });

    // Fetch flyer types for selected campaign
    const { data: campaignFlyers = [] } = useQuery<CampaignFlyer[]>({
        queryKey: ["campaigns", selectedCampaignId, "flyer-types"],
        queryFn: () => fetchFlyerTypes(selectedCampaignId),
        enabled: isAuth && isCampaignSelected(selectedCampaignId),
        staleTime: 5 * 60 * 1000
    });

    const handleCampaignChange = useCallback((campaignId: string) => {
        setSelectedCampaignId(campaignId);
        if (!isCampaignSelected(campaignId)) {
            setSelectedFlyerTypeId(null);
        }
    }, []);

    // Extract FlyerType from CampaignFlyer for MapView
    // Include both flyers with flyerType and campaign-specific flyers
    const flyerTypes = useMemo(() => {
        return campaignFlyers
            .map((cf) => {
                // If it has a flyerType, use that
                if (cf.flyerType) {
                    return cf.flyerType;
                }
                // Otherwise, create a virtual FlyerType from CampaignFlyer properties
                // Use CampaignFlyer.id as the identifier for campaign-specific flyers
                return {
                    id: cf.id, // Use CampaignFlyer.id for campaign-specific flyers
                    name: cf.name,
                    summary: cf.summary,
                    imageUrl: cf.imageUrl
                } as FlyerType;
            })
            .filter((ft): ft is FlyerType => ft !== undefined);
    }, [campaignFlyers]);

    // Get bbox from map if viewportBbox is not set yet
    const effectiveBbox = useMemo(() => {
        if (viewportBbox) {
            return viewportBbox;
        }
        if (mapInstance && isMapLoaded) {
            try {
                const bounds = mapInstance.getBounds();
                if (bounds) {
                    const sw = bounds.getSouthWest();
                    const ne = bounds.getNorthEast();
                    const padding = 0.001;
                    return `${sw.lng - padding},${sw.lat - padding},${ne.lng + padding},${ne.lat + padding}`;
                }
            } catch (error) {
                console.warn("[Shell] Failed to get bounds from map:", error);
            }
        }
        return undefined;
    }, [viewportBbox, mapInstance, isMapLoaded]);

    // Counties (län) for Kartlager dropdown
    const countiesQuery = useCounties({ enabled: isAuth });

    // Municipality list for Kartlager dropdown (all municipalities for map + metadata viewing; no geometry). Same limit as Admin so all kommuner are available.
    const municipalitiesListQuery = useMunicipalities(
        { limit: 2000, countyId: selectedCountyId ?? undefined, includeGeometry: false },
        null,
        { enabled: isAuth }
    );

    const countyOptions = useMemo(() => {
        const data = countiesQuery.data;
        if (!data?.length) return [];
        return data.map((c) => ({ id: c.id, code: c.code, name: c.name }));
    }, [countiesQuery.data]);

    const handleCountyChange = useCallback((countyId: string | null) => {
        setSelectedCountyId(countyId);
        setSelectedMunicipalityId(null);
    }, []);

    const handleMunicipalityChange = useCallback((municipalityId: string | null) => {
        setSelectedMunicipalityId(municipalityId);
        setSelectedSavedSetId(null);
    }, []);

    const municipalityOptions = useMemo(() => {
        const data = municipalitiesListQuery.data;
        if (!data?.features?.length) return [];
        return data.features
            .map((f) => ({
                id: (f.id ?? (f.properties as { municipalityPolygonId?: string })?.municipalityPolygonId) as string,
                name: (f.properties as { name?: string })?.name ?? "",
                code: (f.properties as { code?: string })?.code ?? ""
            }))
            .filter((o) => o.id && o.name)
            .sort((a, b) => a.name.localeCompare(b.name, "sv"));
    }, [municipalitiesListQuery.data]);

    // Resolve municipality code from selected municipality id
    const selectedMunicipalityCode = useMemo(() => {
        if (!selectedMunicipalityId) return undefined;
        return municipalityOptions.find((m) => m.id === selectedMunicipalityId)?.code || undefined;
    }, [selectedMunicipalityId, municipalityOptions]);

    // Fetch selected municipality WITH geometry for fitBounds and highlight (list has includeGeometry: false)
    const selectedMunicipalityGeometryQuery = useMunicipalities(
        {
            codes: selectedMunicipalityCode ?? undefined,
            limit: 1,
            includeGeometry: true
        },
        null,
        { enabled: !!selectedMunicipalityCode && isAuth }
    );

    const savedSetsListQuery = useSavedSetsList({ enabled: isAuth });
    const savedSetDetailsQuery = useSavedSetDetails(selectedSavedSetId ?? undefined, {
        enabled: isAuth && !!selectedSavedSetId
    });

    const { data: currentUser } = useCurrentUser();
    const { data: currentUserRoles = [] } = useUserRoles(currentUser?.id ?? "");
    const canEditSavedSets = useMemo(() => {
        const highest = getHighestRole(currentUserRoles.map((r) => r.role));
        if (!highest) return false;
        return getRoleLevel(highest) <= getRoleLevel("KretsAdmin");
    }, [currentUserRoles]);

    const showSavedSetsSection = useMemo(() => {
        const highest = getHighestRole(currentUserRoles.map((r) => r.role));
        if (!highest) return false;
        const level = getRoleLevel(highest);
        const isKretsAdminOrDistributor =
            level === getRoleLevel("KretsAdmin") || level === getRoleLevel("Distributor");
        if (isKretsAdminOrDistributor) {
            // KretsAdmin or Distributor: show only when viewing own municipality
            const userMunicipalityId = teamContextQuery.data?.municipalityPolygonId;
            return !!userMunicipalityId && selectedMunicipalityId === userMunicipalityId;
        }
        return true; // DistriktAdmin, RiksAdmin, SuperAdmin: always show
    }, [currentUserRoles, teamContextQuery.data?.municipalityPolygonId, selectedMunicipalityId]);

    // When a saved set is selected and its details load, set municipality and clear county so list includes it
    const savedSetMunicipalityId = savedSetDetailsQuery.data?.municipalityPolygonId;
    useEffect(() => {
        if (selectedSavedSetId && savedSetMunicipalityId) {
            setSelectedMunicipalityId(savedSetMunicipalityId);
            setSelectedCountyId(null);
        }
    }, [selectedSavedSetId, savedSetMunicipalityId]);

    // Block postal/electoral queries until saved set's municipality is fully resolved.
    // This prevents queries from firing with the wrong municipality code or stale bbox
    // during the transition when a saved set is selected but details haven't loaded yet.
    const savedSetMunicipalityPending = !!selectedSavedSetId && (
        !savedSetDetailsQuery.data?.municipalityPolygonId ||
        selectedMunicipalityId !== savedSetDetailsQuery.data.municipalityPolygonId ||
        !selectedMunicipalityCode
    );

    // Municipality context for metadata: selected municipality, saved set's municipality, or (for KretsAdmin/Distributor) own team municipality so they always see metadata in Kartlager.
    const metadataMunicipalityId =
        selectedMunicipalityId ??
        savedSetDetailsQuery.data?.municipalityPolygonId ??
        teamContextQuery.data?.municipalityPolygonId ??
        null;

    const electoralMetadataFieldsQuery = useElectoralMetadataFields();
    const municipalityMetadataGridQuery = useMunicipalityMetadataGrid(
        metadataMunicipalityId,
        { enabled: !!metadataMunicipalityId }
    );

    /** Decimal-fält som har minst ett numeriskt värde för vald kommun (för dropdown) */
    const metadataFieldIdsWithValues = useMemo(() => {
        const grid = municipalityMetadataGridQuery.data;
        if (!grid?.values?.length || !grid?.fieldDefinitions?.length) return [];
        const fieldById = new Map(grid.fieldDefinitions.map((f) => [f.id, f]));
        const idsWithValues = new Set<string>();
        for (const cell of grid.values) {
            if (cell.value == null || cell.value === "") continue;
            const def = fieldById.get(cell.fieldDefinitionId);
            if (def?.type === "Decimal") {
                const n = Number.parseFloat(cell.value);
                if (Number.isFinite(n)) idsWithValues.add(cell.fieldDefinitionId);
            }
        }
        return Array.from(idsWithValues);
    }, [municipalityMetadataGridQuery.data]);

    const { electoralMetadataValues, electoralMetadataMinMax } = useMemo(() => {
        const values: Record<string, number> = {};
        let min = 0;
        let max = 100;
        const grid = municipalityMetadataGridQuery.data;
        const fieldId = metadataFieldIdForMap;
        if (!fieldId || !grid?.values?.length) {
            return { electoralMetadataValues: null, electoralMetadataMinMax: { min, max } };
        }
        const numbers: number[] = [];
        for (const cell of grid.values) {
            if (cell.fieldDefinitionId !== fieldId || cell.value == null || cell.value === "") continue;
            const n = Number.parseFloat(cell.value);
            if (!Number.isFinite(n)) continue;
            values[cell.electoralDistrictId] = n;
            numbers.push(n);
        }
        if (numbers.length > 0) {
            min = Math.min(...numbers);
            max = Math.max(...numbers);
            if (min === max) {
                max = min + 1;
            }
        }
        return {
            electoralMetadataValues: Object.keys(values).length > 0 ? values : null,
            electoralMetadataMinMax: { min, max }
        };
    }, [metadataFieldIdForMap, municipalityMetadataGridQuery.data]);

    /** Per-district tooltip data for map (name + all metadata fields with values). Order follows fieldDefinitions (display order), not alphabetical. */
    const electoralDistrictTooltipData = useMemo((): import("../api/electoral-metadata/types").ElectoralDistrictTooltipDataMap | null => {
        const grid = municipalityMetadataGridQuery.data;
        if (!grid?.districts?.length || !grid?.fieldDefinitions?.length) return null;
        const valueByKey = new Map(
            (grid.values ?? []).map((v) => [`${v.electoralDistrictId}|${v.fieldDefinitionId}`, v.value])
        );
        const result: import("../api/electoral-metadata/types").ElectoralDistrictTooltipDataMap = {};
        for (const district of grid.districts) {
            const fields: { fieldName: string; value: string }[] = [];
            for (const def of grid.fieldDefinitions) {
                const value = valueByKey.get(`${district.id}|${def.id}`);
                if (value == null || value === "") continue;
                fields.push({ fieldName: def.name, value });
            }
            result[district.id] = { name: district.name, fields };
        }
        return Object.keys(result).length > 0 ? result : null;
    }, [municipalityMetadataGridQuery.data]);

    // Rensa valt metadatafält om det inte har värden för vald kommun
    useEffect(() => {
        if (metadataFieldIdForMap && metadataFieldIdsWithValues.length > 0 && !metadataFieldIdsWithValues.includes(metadataFieldIdForMap)) {
            setMetadataFieldIdForMap(null);
        }
    }, [metadataFieldIdForMap, metadataFieldIdsWithValues]);

    // Fetch postal codes and electoral districts data
    // Bbox is optional - if not provided, all postal codes will be loaded (up to limit)
    // If bbox is provided, only postal codes within viewport are shown
    const postalCodesQuery = usePostalCodes(
        {
            postalCodes: postalCodesFilter || undefined,
            municipalityCode: selectedMunicipalityCode,
            // When municipality is selected, don't send bbox — backend filters by municipality and result set is bounded.
            // When no municipality, always send viewport bbox so response is small (avoids 413).
            bbox: selectedMunicipalityCode ? undefined : (effectiveBbox ?? undefined)
        },
        selectedMunicipalityCode ? null : mapInstance,
        {
            enabled: showPostalCodes && !!mapInstance && isMapLoaded && (!!effectiveBbox || !!postalCodesFilter || !!selectedMunicipalityCode) && !savedSetMunicipalityPending
        }
    );

    // Show user-facing error when postal codes fail to load (e.g. 413 Content Too Large); only once per error
    const postalCodesErrorShownRef = useRef<string | null>(null);
    useEffect(() => {
        if (postalCodesQuery.isSuccess) postalCodesErrorShownRef.current = null;
    }, [postalCodesQuery.isSuccess]);
    useEffect(() => {
        if (!showPostalCodes || !postalCodesQuery.isError || !postalCodesQuery.error) return;
        const err = postalCodesQuery.error as { response?: { status?: number; data?: { message?: string } } };
        const status = err?.response?.status;
        const key = `${status ?? "err"}`;
        if (postalCodesErrorShownRef.current === key) return;
        postalCodesErrorShownRef.current = key;
        const msg = err?.response?.data?.message;
        if (status === 413) {
            showError(
                "Postnummer kunde inte laddas",
                "Svaret blev för stort. Zooma in på kartan och försök igen."
            );
        } else {
            showError(
                "Kunde inte ladda postnummer",
                msg || (status ? `HTTP ${status}` : undefined)
            );
        }
    }, [showPostalCodes, postalCodesQuery.isError, postalCodesQuery.error]);

    // Debug logging for postal codes
    useEffect(() => {
        if (showPostalCodes) {
            console.log("[Shell] Postal codes query state:", {
                enabled: showPostalCodes && !!mapInstance && isMapLoaded && (!!effectiveBbox || !!postalCodesFilter || !!selectedMunicipalityCode) && !savedSetMunicipalityPending,
                showPostalCodes,
                hasMapInstance: !!mapInstance,
                isMapLoaded,
                hasBbox: !!effectiveBbox,
                hasFilter: !!postalCodesFilter,
                filter: postalCodesFilter,
                savedSetMunicipalityPending,
                selectedMunicipalityCode,
                isLoading: postalCodesQuery.isLoading,
                hasData: !!postalCodesQuery.data,
                featureCount: postalCodesQuery.data?.features.length ?? 0,
                error: postalCodesQuery.error
            });
        }
    }, [showPostalCodes, mapInstance, isMapLoaded, effectiveBbox, postalCodesFilter, postalCodesQuery]);

    const electoralDistrictsQuery = useElectoralDistricts(
        {
            codes: electoralDistrictsFilter || undefined,
            municipalityCode: selectedMunicipalityCode,
            // When municipality is selected, don't send bbox — backend filters by municipality.
            bbox: selectedMunicipalityCode ? undefined : (effectiveBbox ?? undefined)
        },
        selectedMunicipalityCode ? null : mapInstance,
        {
            enabled: showElectoralDistricts && !!mapInstance && isMapLoaded && (!!effectiveBbox || !!electoralDistrictsFilter || !!selectedMunicipalityCode) && !savedSetMunicipalityPending
        }
    );

    const municipalitiesQuery = useMunicipalities(
        {
            codes: municipalitiesFilter || undefined,
            bbox: effectiveBbox
        },
        mapInstance,
        {
            enabled: showMunicipalities && !!mapInstance && isMapLoaded && (!!effectiveBbox || !!municipalitiesFilter)
        }
    );

    // When municipality is selected and we have options, set county from municipality code (first 2 digits = län)
    useEffect(() => {
        if (!selectedMunicipalityId || !countyOptions.length || !municipalityOptions.length) return;
        const mun = municipalityOptions.find((m) => m.id === selectedMunicipalityId);
        if (!mun?.code || mun.code.length < 2) return;
        const countyCode = mun.code.slice(0, 2);
        const county = countyOptions.find((c) => c.code === countyCode);
        if (county) {
            setSelectedCountyId((prev) => (prev === county.id ? prev : county.id));
        }
    }, [selectedMunicipalityId, municipalityOptions, countyOptions]);

    const togglePostalCodeMutation = useTogglePostalCode();
    const toggleElectoralDistrictMutation = useTogglePriorityElectoralDistrict();

    const handleTogglePostalCode = useCallback(
        (postalCodePolygonId: string) => {
            if (!selectedSavedSetId) {
                setTemporaryPostalCodeIds((prev) =>
                    prev.includes(postalCodePolygonId)
                        ? prev.filter((id) => id !== postalCodePolygonId)
                        : [...prev, postalCodePolygonId]
                );
                return;
            }
            togglePostalCodeMutation.mutate({ savedSetId: selectedSavedSetId, postalCodeId: postalCodePolygonId });
        },
        [selectedSavedSetId, togglePostalCodeMutation]
    );
    const handleTogglePriorityElectoralDistrict = useCallback(
        (electoralDistrictId: string) => {
            if (!selectedSavedSetId) {
                setTemporaryElectoralDistrictIds((prev) =>
                    prev.includes(electoralDistrictId)
                        ? prev.filter((id) => id !== electoralDistrictId)
                        : [...prev, electoralDistrictId]
                );
                return;
            }
            toggleElectoralDistrictMutation.mutate({ savedSetId: selectedSavedSetId, districtId: electoralDistrictId });
        },
        [selectedSavedSetId, toggleElectoralDistrictMutation]
    );
    const deleteSavedSetMutation = useDeleteSavedSet();
    const handleDeleteSavedSet = useCallback(
        async (id: string) => {
            try {
                await deleteSavedSetMutation.mutateAsync(id);
                setSelectedSavedSetId(null);
            } catch (err) {
                showError(
                    t("map.layers.deleteSavedSetError", "Could not delete saved set"),
                    (err as Error)?.message ?? ""
                );
            }
        },
        [deleteSavedSetMutation, t]
    );
    const handleCreateSavedSet = useCallback(() => setCreateSavedSetDialogOpen(true), []);
    const handleUpdateSavedSet = useCallback(() => setUpdateSavedSetDialogOpen(true), []);

    const handleMetadataFieldForMapChange = useCallback((fieldId: string | null) => {
        setMetadataFieldIdForMap(fieldId);
        if (fieldId) {
            setShowElectoralDistricts(true);
        }
    }, []);

    const handleClickPostalCodesEnabledChange = useCallback(() => {
        setClickPostalCodesEnabled((prev) => {
            const next = !prev;
            if (next) {
                setShowPostalCodes(true);
            }
            return next;
        });
        setClickElectoralDistrictsEnabled(false);
    }, []);

    const handleClickElectoralDistrictsEnabledChange = useCallback(() => {
        setClickElectoralDistrictsEnabled((prev) => {
            const next = !prev;
            if (next) {
                setShowElectoralDistricts(true);
            }
            return next;
        });
        setClickPostalCodesEnabled(false);
    }, []);

    // Use geometry query so we have polygon for highlight and fitBounds (list has no geometry)
    const selectedMunicipalityFeature = useMemo(() => {
        if (!selectedMunicipalityId) return null;
        const features = selectedMunicipalityGeometryQuery.data?.features;
        if (!features?.length) return null;
        const feature = features.find(
            (f) =>
                (f.id != null && String(f.id) === selectedMunicipalityId) ||
                (f.properties as { municipalityPolygonId?: string })?.municipalityPolygonId === selectedMunicipalityId
        );
        return feature ? (feature as import("../api/map/types").GeoJsonFeature<import("../api/map/types").MunicipalityFeatureProperties>) : (features[0] as import("../api/map/types").GeoJsonFeature<import("../api/map/types").MunicipalityFeatureProperties>);
    }, [selectedMunicipalityId, selectedMunicipalityGeometryQuery.data?.features]);

    const temporarySelectedPostalCodes = useMemo(() => {
        const features = postalCodesQuery.data?.features ?? [];
        const byId = new Map(
            features.map((feature) => [
                String(feature.id ?? feature.properties.postalCodePolygonId),
                feature.properties
            ])
        );
        return temporaryPostalCodeIds.map((id) => {
            const props = byId.get(id);
            return {
                postalCodePolygonId: id,
                postalCode: props?.postalCode,
                locality: props?.locality ?? null,
                householdCount: props?.addressCount ?? null
            };
        });
    }, [temporaryPostalCodeIds, postalCodesQuery.data?.features]);

    const temporarySelectedElectoralDistricts = useMemo(() => {
        const features = electoralDistrictsQuery.data?.features ?? [];
        const byId = new Map(
            features.map((feature) => [
                String(feature.id ?? feature.properties.electoralDistrictId),
                feature.properties
            ])
        );
        return temporaryElectoralDistrictIds.map((id) => {
            const props = byId.get(id);
            return {
                electoralDistrictId: id,
                name: props?.name,
                code: props?.code
            };
        });
    }, [temporaryElectoralDistrictIds, electoralDistrictsQuery.data?.features]);

    const fitBounds = useMemo(() => {
        const features = selectedMunicipalityGeometryQuery.data?.features;
        if (!features?.length) return null;
        const municipalityId = selectedSavedSetId && savedSetDetailsQuery.data?.municipalityPolygonId
            ? savedSetDetailsQuery.data.municipalityPolygonId
            : selectedMunicipalityId;
        if (!municipalityId) return null;
        const feature = features.find(
            (f) =>
                (f.id != null && String(f.id) === municipalityId) ||
                (f.properties as { municipalityPolygonId?: string })?.municipalityPolygonId === municipalityId
        );
        const f = feature ?? features[0];
        if (!f) return null;
        return getBoundingBoxFromFeature(f as import("../api/map/types").GeoJsonFeature<unknown>);
    }, [
        selectedMunicipalityId,
        selectedSavedSetId,
        savedSetDetailsQuery.data?.municipalityPolygonId,
        selectedMunicipalityGeometryQuery.data?.features
    ]);

    // No auto-login - user must log in via Login component

    const switchLanguage = useCallback(
        (language: "sv" | "en") => {
            void i18n.changeLanguage(language);
        },
        [i18n]
    );

    const handleLogout = useCallback(async () => {
        try {
            // Invalidate and remove auth status query specifically
            queryClient.removeQueries({ queryKey: ["auth", "status"] });
            queryClient.cancelQueries({ queryKey: ["auth", "status"] });

            // Clear all cached data and cancel all queries
            queryClient.clear();
            queryClient.cancelQueries(); // Cancel any in-flight requests

            // Call logout - it will handle redirect
            await logout();
        } catch (error) {
            // Fallback: force redirect even if logout fails
            console.error("Logout failed:", error);
            // Clear cache and redirect immediately
            queryClient.removeQueries({ queryKey: ["auth", "status"] });
            queryClient.clear();
            queryClient.cancelQueries();
            window.location.replace("/login");
        }
    }, [queryClient]);

    return (
        <MapContextProvider
            map={mapContextValue.map}
            isMapLoaded={mapContextValue.isMapLoaded}
            isDrawingPolygon={mapContextValue.isDrawingPolygon}
            polygonArea={mapContextValue.polygonArea}
            highlightedAssignmentArea={mapContextValue.highlightedAssignmentArea}
            highlightedAssignmentId={mapContextValue.highlightedAssignmentId}
            onHighlightedAssignmentAreaChange={mapContextValue.setHighlightedAssignmentArea}
            assignmentHighlightNonce={mapContextValue.assignmentHighlightNonce}
            onStartDrawing={mapContextValue.startPolygonDrawing}
            onStopDrawing={mapContextValue.stopPolygonDrawing}
            onAreaChange={mapContextValue.setPolygonArea}
        >
            <div className={`app-shell ${isMobileApp ? 'app-shell--mobile-app' : ''}`}>
                <header className="app-shell__header">
                    <div>
                        <h1>{t("app.title")}</h1>
                        <p className="app-shell__subtitle">{t("app.subtitle")}</p>
                    </div>

                    <div className="app-shell__header-actions" role="group" aria-label="Meny">
                        <WwwPortalNavBar surface="www_map" />
                        {showMapOnlyActions ? (
                            <div className="app-shell__map-only-toolbar">
                                <Link to="/admin" className="app-shell__admin-link">
                                    {t("app.menu.admin")}
                                </Link>
                                <Link to="/change-password" className="app-shell__admin-link">
                                    {t("app.changePassword.nav")}
                                </Link>
                                <button
                                    type="button"
                                    className={`app-shell__lang-button ${i18n.language === "sv" || i18n.language?.startsWith("sv") ? "app-shell__lang-button--active" : ""}`}
                                    onClick={() => switchLanguage("sv")}
                                >
                                    {t("app.languageSwitch.sv")}
                                </button>
                                <button
                                    type="button"
                                    className={`app-shell__lang-button ${i18n.language === "en" || i18n.language?.startsWith("en") ? "app-shell__lang-button--active" : ""}`}
                                    onClick={() => switchLanguage("en")}
                                >
                                    {t("app.languageSwitch.en")}
                                </button>
                                <button
                                    type="button"
                                    className="app-shell__logout-button"
                                    onClick={handleLogout}
                                    title={t("app.logout")}
                                >
                                    {t("app.logout")}
                                </button>
                            </div>
                        ) : null}
                    </div>

                    <MobileMenu
                        onLogout={handleLogout}
                        onLanguageChange={switchLanguage}
                        currentLanguage={i18n.language}
                        isMobileApp={isMobileApp}
                        onKartlagerClick={() => setShowMobileLayers(true)}
                        showMapOnlyActions={showMapOnlyActions}
                    />
                </header>

                <main className={`app-shell__main ${hasMapCampaignScope(selectedCampaignId) ? 'app-shell__main--campaign-selected' : ''}`}>
                    <div className="app-shell__content">
                        <section className="app-shell__map-section" aria-label={t("map.title")}>
                            <MapView
                                campaignId={selectedCampaignId}
                                municipalityId={selectedMunicipalityId}
                                flyerTypeId={selectedFlyerTypeId}
                                campaigns={campaigns}
                                flyerTypes={flyerTypes}
                                onCampaignChange={handleCampaignChange}
                                onFlyerTypeChange={setSelectedFlyerTypeId}
                                onMapReady={handleMapReady}
                                showFilterPanel={!isMobileApp}
                                showAssignments={showAssignments}
                                onAssignmentsDataChange={setAssignmentsData}
                                showPostalCodes={showPostalCodes}
                                showElectoralDistricts={showElectoralDistricts}
                                showMunicipalities={showMunicipalities}
                                postalCodesData={postalCodesQuery.data ?? null}
                                electoralDistrictsData={electoralDistrictsQuery.data ?? null}
                                municipalitiesData={municipalitiesQuery.data ?? null}
                                selectedMunicipalityId={selectedMunicipalityId}
                                selectedMunicipalityFeature={selectedMunicipalityFeature}
                                onViewportBboxChange={setViewportBbox}
                                fitBounds={fitBounds ?? undefined}
                                selectedPostalCodeIds={
                                    selectedSavedSetId
                                        ? (savedSetDetailsQuery.data?.postalCodes?.map((p) => p.postalCodePolygonId) ?? [])
                                        : temporaryPostalCodeIds
                                }
                                selectedElectoralDistrictIds={
                                    selectedSavedSetId
                                        ? (savedSetDetailsQuery.data?.priorityElectoralDistricts?.map((d) => d.electoralDistrictId) ?? [])
                                        : temporaryElectoralDistrictIds
                                }
                                onTogglePostalCode={handleTogglePostalCode}
                                onTogglePriorityElectoralDistrict={handleTogglePriorityElectoralDistrict}
                                clickPostalCodesEnabled={clickPostalCodesEnabled}
                                clickElectoralDistrictsEnabled={clickElectoralDistrictsEnabled}
                                electoralMetadataValues={electoralMetadataValues ?? undefined}
                                electoralMetadataFieldId={metadataFieldIdForMap ?? undefined}
                                electoralMetadataMinMax={showMetadataOnMap && metadataFieldIdForMap ? electoralMetadataMinMax : null}
                                electoralDistrictTooltipData={showMetadataOnMap ? (electoralDistrictTooltipData ?? undefined) : undefined}
                                showDistributorPins={showAssignments}
                                distributorPinsMunicipalityId={selectedMunicipalityId}
                            />
                            {/* Mobile app selection bar - shows selected campaign/flyer at bottom */}
                            {isMobileApp && hasMapCampaignScope(selectedCampaignId) && (
                                <div className="mobile-selection-bar">
                                    <span className="mobile-selection-bar__campaign">
                                        {isAllCampaignsMode(selectedCampaignId)
                                            ? t("map.filter.allCampaigns")
                                            : campaigns.find(c => c.id === selectedCampaignId)?.name || t("map.filter.selectCampaign")}
                                    </span>
                                    {selectedFlyerTypeId && (
                                        <span className="mobile-selection-bar__flyer">
                                            {flyerTypes.find(f => f?.id === selectedFlyerTypeId)?.name}
                                        </span>
                                    )}
                                </div>
                            )}
                        </section>
                    </div>
                    <aside className="app-shell__sidebar app-shell__sidebar--desktop" aria-label={t("sidebar.title")}>
                        {!isNarrowLayout && (
                        <MapLayersPanel
                            showPostalCodes={showPostalCodes}
                            showElectoralDistricts={showElectoralDistricts}
                            showMunicipalities={showMunicipalities}
                            showAssignments={showAssignments}
                            assignmentsData={assignmentsData}
                            assignmentsNeedsMunicipality={!selectedMunicipalityId && !hasMapCampaignScope(selectedCampaignId)}
                            postalCodesFilter={postalCodesFilter}
                            electoralDistrictsFilter={electoralDistrictsFilter}
                            municipalitiesFilter={municipalitiesFilter}
                            onTogglePostalCodes={setShowPostalCodes}
                            onToggleElectoralDistricts={setShowElectoralDistricts}
                            onToggleMunicipalities={setShowMunicipalities}
                            onToggleAssignments={handleToggleAssignments}
                            onPostalCodesFilterChange={setPostalCodesFilter}
                            onElectoralDistrictsFilterChange={setElectoralDistrictsFilter}
                            onMunicipalitiesFilterChange={setMunicipalitiesFilter}
                            countyOptions={countyOptions}
                            selectedCountyId={selectedCountyId}
                            onCountyChange={handleCountyChange}
                            countiesLoading={countiesQuery.isLoading}
                            countiesError={countiesQuery.error}
                            municipalityOptions={municipalityOptions}
                            selectedMunicipalityId={selectedMunicipalityId}
                            onMunicipalityChange={handleMunicipalityChange}
                            municipalitiesLoading={municipalitiesListQuery.isLoading}
                            municipalitiesError={municipalitiesListQuery.error}
                            savedSets={savedSetsListQuery.data ?? []}
                            selectedSavedSetId={selectedSavedSetId}
                            onSavedSetChange={setSelectedSavedSetId}
                            savedSetsLoading={savedSetsListQuery.isLoading}
                            savedSetsError={savedSetsListQuery.error}
                            savedSetDetails={savedSetDetailsQuery.data ?? null}
                            savedSetDetailsLoading={savedSetDetailsQuery.isLoading}
                            onDeleteSavedSet={handleDeleteSavedSet}
                            onCreateSavedSet={handleCreateSavedSet}
                            onUpdateSavedSet={handleUpdateSavedSet}
                            canEditSavedSets={canEditSavedSets}
                            showSavedSetsSection={showSavedSetsSection}
                            clickPostalCodesEnabled={clickPostalCodesEnabled}
                            onClickPostalCodesEnabledChange={handleClickPostalCodesEnabledChange}
                            clickElectoralDistrictsEnabled={clickElectoralDistrictsEnabled}
                            onClickElectoralDistrictsEnabledChange={handleClickElectoralDistrictsEnabledChange}
                            temporarySelectedPostalCodes={temporarySelectedPostalCodes}
                            temporarySelectedElectoralDistricts={temporarySelectedElectoralDistricts}
                            electoralMetadataFields={electoralMetadataFieldsQuery.data ?? []}
                            metadataFieldIdsWithValues={metadataFieldIdsWithValues}
                            metadataMunicipalityId={metadataMunicipalityId}
                            metadataGridLoading={municipalityMetadataGridQuery.isLoading}
                            metadataGridError={municipalityMetadataGridQuery.isError ? (municipalityMetadataGridQuery.error ?? new Error("Failed to load metadata")) : null}
                            metadataFieldIdForMap={metadataFieldIdForMap}
                            onMetadataFieldForMapChange={handleMetadataFieldForMapChange}
                            showMetadataOnMap={showMetadataOnMap}
                            onShowMetadataOnMapChange={setShowMetadataOnMap}
                            hasMetadataMunicipality={!!metadataMunicipalityId}
                            electoralMetadataMinMax={electoralMetadataMinMax}
                        />
                        )}
                        {SHOW_ACTIVITY_LOG && <ActivityPanel />}
                    </aside>
                </main>
            </div>
            <SavedSetCreateDialog
                open={createSavedSetDialogOpen}
                onClose={() => setCreateSavedSetDialogOpen(false)}
                municipalityOptions={municipalityOptions}
                selectedMunicipalityId={selectedMunicipalityId}
                onSuccess={(newId) => setSelectedSavedSetId(newId)}
            />
            <SavedSetUpdateDialog
                open={updateSavedSetDialogOpen}
                onClose={() => setUpdateSavedSetDialogOpen(false)}
                savedSet={savedSetDetailsQuery.data ?? null}
                municipalityOptions={municipalityOptions}
                onSuccess={() => {}}
            />
            {showMobileLayers &&
                isNarrowLayout &&
                createPortal(
                    <div
                        className="mobile-layers-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-label={t("map.layers.title")}
                    >
                        <div
                            className="mobile-layers-overlay__backdrop"
                            onClick={() => setShowMobileLayers(false)}
                            aria-hidden="true"
                        />
                        <div className="mobile-layers-overlay__panel">
                            <div className="mobile-layers-overlay__header">
                                <h2 className="mobile-layers-overlay__title">{t("map.layers.title")}</h2>
                                <button
                                    type="button"
                                    className="mobile-layers-overlay__close"
                                    onClick={() => setShowMobileLayers(false)}
                                    aria-label={t("app.menu.close")}
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="mobile-layers-overlay__content">
                                <MapLayersPanel
                                    showPostalCodes={showPostalCodes}
                                    showElectoralDistricts={showElectoralDistricts}
                                    showMunicipalities={showMunicipalities}
                                    showAssignments={showAssignments}
                                    assignmentsData={assignmentsData}
                                    assignmentsNeedsMunicipality={!selectedMunicipalityId && !hasMapCampaignScope(selectedCampaignId)}
                                    postalCodesFilter={postalCodesFilter}
                                    electoralDistrictsFilter={electoralDistrictsFilter}
                                    municipalitiesFilter={municipalitiesFilter}
                                    onTogglePostalCodes={setShowPostalCodes}
                                    onToggleElectoralDistricts={setShowElectoralDistricts}
                                    onToggleMunicipalities={setShowMunicipalities}
                                    onToggleAssignments={handleToggleAssignments}
                                    onPostalCodesFilterChange={setPostalCodesFilter}
                                    onElectoralDistrictsFilterChange={setElectoralDistrictsFilter}
                                    onMunicipalitiesFilterChange={setMunicipalitiesFilter}
                                    countyOptions={countyOptions}
                                    selectedCountyId={selectedCountyId}
                                    onCountyChange={handleCountyChange}
                                    countiesLoading={countiesQuery.isLoading}
                                    countiesError={countiesQuery.error}
                                    municipalityOptions={municipalityOptions}
                                    selectedMunicipalityId={selectedMunicipalityId}
                                    onMunicipalityChange={handleMunicipalityChange}
                                    municipalitiesLoading={municipalitiesListQuery.isLoading}
                                    municipalitiesError={municipalitiesListQuery.error}
                                    savedSets={savedSetsListQuery.data ?? []}
                                    selectedSavedSetId={selectedSavedSetId}
                                    onSavedSetChange={setSelectedSavedSetId}
                                    savedSetsLoading={savedSetsListQuery.isLoading}
                                    savedSetsError={savedSetsListQuery.error}
                                    savedSetDetails={savedSetDetailsQuery.data ?? null}
                                    savedSetDetailsLoading={savedSetDetailsQuery.isLoading}
                                    onDeleteSavedSet={handleDeleteSavedSet}
                                    onCreateSavedSet={handleCreateSavedSet}
                                    onUpdateSavedSet={handleUpdateSavedSet}
                                    canEditSavedSets={canEditSavedSets}
                                    showSavedSetsSection={showSavedSetsSection}
                                    clickPostalCodesEnabled={clickPostalCodesEnabled}
                                    onClickPostalCodesEnabledChange={handleClickPostalCodesEnabledChange}
                                    clickElectoralDistrictsEnabled={clickElectoralDistrictsEnabled}
                                    onClickElectoralDistrictsEnabledChange={handleClickElectoralDistrictsEnabledChange}
                                    temporarySelectedPostalCodes={temporarySelectedPostalCodes}
                                    temporarySelectedElectoralDistricts={temporarySelectedElectoralDistricts}
                                    electoralMetadataFields={electoralMetadataFieldsQuery.data ?? []}
                                    metadataFieldIdsWithValues={metadataFieldIdsWithValues}
                                    metadataMunicipalityId={metadataMunicipalityId}
                                    metadataGridLoading={municipalityMetadataGridQuery.isLoading}
                                    metadataGridError={
                                        municipalityMetadataGridQuery.isError
                                            ? (municipalityMetadataGridQuery.error ?? new Error("Failed to load metadata"))
                                            : null
                                    }
                                    metadataFieldIdForMap={metadataFieldIdForMap}
                                    onMetadataFieldForMapChange={handleMetadataFieldForMapChange}
                                    showMetadataOnMap={showMetadataOnMap}
                                    onShowMetadataOnMapChange={setShowMetadataOnMap}
                                    hasMetadataMunicipality={!!metadataMunicipalityId}
                                    electoralMetadataMinMax={electoralMetadataMinMax}
                                />
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </MapContextProvider>
    );
};